import { addDoc, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { PaginatedTable } from '@/components/ui/PaginatedTable';
import { useOrg } from '@/contexts/OrgContext';
import { getFirestoreDb } from '@/lib/firebase';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';
import { collection as fsCollection, getDocs, orderBy, query } from 'firebase/firestore';

type BankAccount = { id: string; name: string; code: string };

type Txn = {
  id: string;
  date: string;
  amount: string;
  memo?: string;
  reconciled: boolean;
};

export function BankingPage() {
  const { t } = useTranslation();
  const { orgId } = useOrg();
  const online = useOnlineStatus();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [name, setName] = useState('');

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    void (async () => {
      const snap = await getDocs(
        query(
          fsCollection(getFirestoreDb(), 'organizations', orgId, 'bankAccounts'),
          orderBy('name')
        )
      );
      if (cancelled) return;
      const list = snap.docs.map((d) => ({
        id: d.id,
        name: String(d.data().name ?? ''),
        code: String(d.data().code ?? '')
      }));
      setAccounts(list);
      if (list.length && !selected) setSelected(list[0]!.id);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const mapTxn = useCallback(
    (id: string, data: Record<string, unknown>) => ({
      id,
      date: String(data.date ?? ''),
      amount: String(data.amount ?? '0.00'),
      memo: data.memo ? String(data.memo) : undefined,
      reconciled: Boolean(data.reconciled)
    }),
    []
  );

  const txns = useFirestorePageQuery<Txn>({
    orgId,
    collectionPath: selected
      ? ['bankAccounts', selected, 'transactions']
      : ['bankAccounts', '_none', 'transactions'],
    orderByField: 'date',
    orderDirection: 'desc',
    mapDoc: mapTxn,
    enabled: Boolean(orgId && selected)
  });

  async function addAccount() {
    if (!orgId || !online || !name.trim()) return;
    const ref = await addDoc(collection(getFirestoreDb(), 'organizations', orgId, 'bankAccounts'), {
      name: name.trim(),
      code: '1100',
      createdAt: serverTimestamp()
    });
    setAccounts((prev) => [...prev, { id: ref.id, name: name.trim(), code: '1100' }]);
    setSelected(ref.id);
    setName('');
  }

  async function addTxn() {
    if (!orgId || !selected || !online) return;
    await addDoc(
      collection(getFirestoreDb(), 'organizations', orgId, 'bankAccounts', selected, 'transactions'),
      {
        date: new Date().toISOString().slice(0, 10),
        amount: '0.00',
        memo: 'Manual',
        reconciled: false,
        createdAt: serverTimestamp()
      }
    );
    txns.refresh();
  }

  async function toggleReconciled(txnId: string, reconciled: boolean) {
    if (!orgId || !selected || !online) return;
    await updateDoc(
      doc(getFirestoreDb(), 'organizations', orgId, 'bankAccounts', selected, 'transactions', txnId),
      { reconciled: !reconciled, updatedAt: serverTimestamp() }
    );
    txns.refresh();
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('nav.banking')}</h1>
      <div className="mt-4 flex gap-2">
        <input
          className="rounded border px-3 py-2"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('banking.accountName')}
        />
        <Button onClick={() => void addAccount()} disabled={!online}>
          {t('banking.addAccount')}
        </Button>
      </div>
      {accounts.length > 0 ? (
        <SegmentedControl
          className="mt-4"
          items={accounts.map((a) => ({ id: a.id, label: a.name }))}
          value={selected ?? accounts[0]!.id}
          onChange={setSelected}
        />
      ) : null}
      {selected && (
        <div className="mt-6">
          <Button variant="secondary" onClick={() => void addTxn()} disabled={!online}>
            {t('banking.addTxn')}
          </Button>
          <PaginatedTable
            className="mt-4"
            loading={txns.loading}
            pageIndex={txns.pageIndex}
            hasNext={txns.hasNext}
            hasPrev={txns.hasPrev}
            onNext={txns.nextPage}
            onPrev={txns.prevPage}
            pageSize={txns.pageSize}
            onPageSizeChange={txns.setPageSize}
          >
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="p-2 text-start">{t('journals.date')}</th>
                  <th className="p-2 text-end">{t('sales.amount')}</th>
                  <th className="p-2">{t('banking.reconciled')}</th>
                </tr>
              </thead>
              <tbody>
                {txns.rows.map((tx) => (
                  <tr key={tx.id} className="border-t border-[var(--fv-border)]">
                    <td className="p-2">{tx.date}</td>
                    <td className="p-2 text-end"><MoneyDisplay value={tx.amount} /></td>
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={tx.reconciled}
                        onChange={() => void toggleReconciled(tx.id, tx.reconciled)}
                        disabled={!online}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PaginatedTable>
        </div>
      )}
    </div>
  );
}
